
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, API_ENDPOINTS } from './utils/baseurl';
import SortableGrid from './components/SortableGrid';
import { useToast } from './contexts/ToastContext';

// Custom Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel", isLoading = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
              disabled={isLoading}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-600">{message}</p>
          </div>
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Success Modal Component
const SuccessModal = ({ isOpen, onClose, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-600">{message}</p>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-indigo-900 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoanDashboard = () => {
  const { showError } = useToast();
  const subscriptionToastShownRef = useRef(false);
  const [loans, setLoans] = useState([]);
  const [allLoans, setAllLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState({
    loanNumber: '',
    mobileNumber: '',
    holderName: '',       
    status: 'A'  // Default to Active status
  });
  // Server-side sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    loanId: null,
    loanNumber: ''
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  // Domain values state
  const [domainValues, setDomainValues] = useState([]);
  const [domainValuesLoaded, setDomainValuesLoaded] = useState(false);
  
  // Summary state
  const [loanSummary, setLoanSummary] = useState({
    totalLoans: 0,
    totalLoanAmount: 0,
    totalGoldWeight: 0,
    status: 'A'
  });

  // Add loading state for summary
  const [summaryLoading, setSummaryLoading] = useState(false);

  const pageSize = 10;

  // Column configuration for SortableGrid
  const loanColumns = [
    {
      key: 'loanNumber',
      label: 'Loan Number',
      sortable: true,
      className: 'font-bold text-indigo-900'
    },
    {
      key: 'loanHolderName',
      label: 'Name',
      sortable: true
    },
    {
      key: 'mobileNumber',
      label: 'Mobile',
      sortable: true
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      render: (item) => `₹${item.amount?.toLocaleString()}`
    },
    {
      key: 'goldWeight',
      label: 'Gold Weight',
      sortable: true,
      render: (item) => {
        const weight = parseFloat(item.goldWeight) || 0;
        return `${weight.toFixed(2)} grams`;
      }
    },
    {
      key: 'loanStartDate',
      label: 'Start Date',
      sortable: true,
      render: (item) => item.loanStartDate || 'N/A'
    },
    {
      key: 'loanDueDate',
      label: 'Due Date',
      sortable: true,
      render: (item) => {
        if (!item.loanDueDate) return 'N/A';
        
        const dueDate = new Date(item.loanDueDate);
        const today = new Date();
        const isOverdue = dueDate < today;
        
        return (
          <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-gray-900'}>
            {item.loanDueDate}
            {isOverdue && <span className="ml-1 text-xs">(Overdue)</span>}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (item) => (
        <span className={getStatusBadge(item.status)}>
          {getLoanStatusOptions().find(option => option.value === item.status)?.label || item.status}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Action',
      sortable: false,
      render: (item) => (
        <div className="flex gap-1 md:gap-2">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(item.id, item.loanNumber);
            }}
            className="p-1 text-indigo-900 hover:text-indigo-700 transition-colors"
            title="Edit loan"
          >
            <Edit size={14} className="md:w-4 md:h-4" />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              showDeleteConfirmation(item.id, item.loanNumber);
            }}
            disabled={deleteLoading === item.id}
            className="p-1 text-red-600 hover:text-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete loan"
          >
            <Trash2 size={14} className="md:w-4 md:h-4" />
          </button>
        </div>
      )
    }
  ];



  // Fetch loans with server-side search and sorting
  const fetchLoans = useCallback(async (filters = {}, pageIndex = 0, sortKey = null, sortDirection = null) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        pageIndex: pageIndex.toString(),
        pageSize: pageSize.toString(),
        ...(filters.loanNumber && { LoanNumber: filters.loanNumber }),
        ...(filters.mobileNumber && { MobileNumber: filters.mobileNumber }),
        ...(filters.holderName && { Name: filters.holderName }),
        ...(filters.status && { Status: filters.status }),
        // Add sorting parameters
        ...(sortKey && { sortBy: sortKey }),
        ...(sortDirection && { sortDirection: sortDirection })
      });

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.LOAN_SEARCH)}?${params.toString()}`);
      const result = await response.json();

      if (result.data) {
        setLoans(result.data);
        setTotalRecords(result.totalRecords || result.data.length);
        setTotalPages(Math.ceil((result.totalRecords || result.data.length) / pageSize));
        setCurrentPage(pageIndex);
      } else if (Array.isArray(result)) {
        // Handle case where API returns array directly
        setLoans(result);
        setTotalRecords(result.length);
        setTotalPages(Math.ceil(result.length / pageSize));
        setCurrentPage(pageIndex);
      } else {
        setLoans([]);
        setTotalRecords(0);
        setTotalPages(0);
        setCurrentPage(0);
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      setLoans([]);
      setTotalRecords(0);
      setTotalPages(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  // Fetch loan summary from backend
  const fetchLoanSummary = useCallback(async (status = null) => {
    try {
      setSummaryLoading(true); // Set loading to true when fetching summary
      const params = new URLSearchParams();
      if (status) {
        params.append('status', status);
      }

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.LOAN_SUMMARY)}?${params.toString()}`);
      if (response.ok) {
        const summary = await response.json();
        setLoanSummary(summary);
        return summary;
      } else {
        setLoanSummary({
          totalLoans: 0,
          totalLoanAmount: 0,
          totalGoldWeight: 0,
          status: status || 'A'
        });
        return null;
      }
    } catch (error) {
      console.error('Error fetching loan summary:', error);
      setLoanSummary({
        totalLoans: 0,
        totalLoanAmount: 0,
        totalGoldWeight: 0,
        status: status || 'A'
      });
      return null;
    } finally {
      setSummaryLoading(false); // Set loading to false after fetching summary
    }
  }, []);

  // Fetch all loans for summary calculations (keeping for backward compatibility)
  const fetchAllLoans = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams({
        pageIndex: '0',
        pageSize: '10000', // Large number to get all records
        ...(filters.loanNumber && { LoanNumber: filters.loanNumber }),
        ...(filters.mobileNumber && { MobileNumber: filters.mobileNumber }),
        ...(filters.holderName && { Name: filters.holderName }),
        ...(filters.status && { Status: filters.status })
      });

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.LOAN_SEARCH)}?${params.toString()}`);
      const result = await response.json();

      if (result.data) {
        setAllLoans(result.data);
      } else if (Array.isArray(result)) {
        setAllLoans(result);
      } else {
        setAllLoans([]);
      }
    } catch (error) {
      console.error('Error fetching all loans:', error);
      setAllLoans([]);
    }
  }, []);

  // Fetch domain values from server with retry mechanism
  const fetchDomainValues = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      // Get access token from localStorage
      const token = checkAccessToken();
      
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      const response = await fetch(buildApiUrl(API_ENDPOINTS.DOMAIN_VALUES), {
        method: 'GET',
        headers: headers,
        cache: 'no-cache' // Prevent caching issues
      });
      
      if (response.ok) {
        const data = await response.json();
        setDomainValues(data);
        setDomainValuesLoaded(true);
        return data;
      } else {
        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          setTimeout(() => {
            fetchDomainValues(retryCount + 1);
          }, 2000);
          return [];
        } else {
          setDomainValuesLoaded(true); // Set to true even on error to prevent infinite loading
          return [];
        }
      }
    } catch (error) {
      // Retry if we haven't exceeded max retries
      if (retryCount < maxRetries) {
        setTimeout(() => {
          fetchDomainValues(retryCount + 1);
        }, 2000);
        return [];
      } else {
        setDomainValuesLoaded(true); // Set to true even on error to prevent infinite loading
        return [];
      }
    }
  }, []);

  // Get loan status options from domain values (domainID: 10 for loan status)
  const getLoanStatusOptions = useCallback(() => {
    const loanStatusValues = domainValues.filter(item => item.domainID === 10 && item.isActive);
    
    // Fallback options in case API fails or returns no data
    const fallbackOptions = [
      { value: 'A', label: 'Active' },
      { value: 'C', label: 'Closed' }
    ];
    
    const options = loanStatusValues.length > 0 
      ? loanStatusValues.map(item => ({
          value: item.code,
          label: item.displayName
        }))
      : fallbackOptions;
    
    return options;
  }, [domainValues]);

  // Helper function to check if access token is available
  const checkAccessToken = () => {
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (!token) {
      // Note: This component doesn't have showModal, so we'll just log the warning
    }
    return token;
  };

  // Check subscription status from API
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const token = checkAccessToken();
      if (!token) return;

      const response = await fetch(buildApiUrl(API_ENDPOINTS.SUBSCRIPTION_CHECK), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Show toast if subscription is not active
        if (data.isSubscriptionActive === false && !subscriptionToastShownRef.current) {
          subscriptionToastShownRef.current = true;
          showError(
            data.adminMessage || 'Your subscription may be due. Please pay it before the due date.',
            'Subscription Notice',
            { autoHideDuration: 8000 } // Auto-close after 8 seconds
          );
        }
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  }, [showError]);

  useEffect(() => {
    // Reset the ref when component mounts to allow checking subscription on each navigation
    subscriptionToastShownRef.current = false;
    
    const initializeData = async () => {
      await fetchDomainValues();
      // Pass the default search filters to ensure only Active loans are loaded initially
      const defaultFilters = { status: 'A', loanNumber: '', mobileNumber: '', holderName: '' };
      fetchLoans(defaultFilters);
      fetchAllLoans(defaultFilters);
      fetchLoanSummary('A');
      
      // Check subscription status from API
      await checkSubscriptionStatus();
    };
    
    initializeData();
  }, [fetchLoans, fetchAllLoans, fetchDomainValues, fetchLoanSummary, checkSubscriptionStatus]);

  // Remove the automatic summary update when status changes
  // useEffect(() => {
  //   fetchLoanSummary(searchFilters.status);
  // }, [searchFilters.status, fetchLoanSummary]);

  // Show delete confirmation modal
  const showDeleteConfirmation = (loanId, loanNumber) => {
    setConfirmModal({
      isOpen: true,
      loanId: loanId,
      loanNumber: loanNumber
    });
  };

  // Close confirmation modal
  const closeConfirmModal = () => {
    if (deleteLoading) return; // Prevent closing while deleting
    setConfirmModal({
      isOpen: false,
      loanId: null,
      loanNumber: ''
    });
  };

  // Show success modal
  const showSuccessModal = (title, message) => {
    setSuccessModal({
      isOpen: true,
      title: title,
      message: message
    });
  };

  // Close success modal
  const closeSuccessModal = () => {
    setSuccessModal({
      isOpen: false,
      title: '',
      message: ''
    });
  };

  // Delete loan function
  const handleDelete = async () => {
    const { loanId } = confirmModal;
    
    try {
      setDeleteLoading(loanId);

      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_DELETE(loanId)));
      
      if (response.ok) {
        // Refresh the data from server after successful deletion
        await fetchLoans(searchFilters, currentPage);
        
        // Close confirmation modal
        closeConfirmModal();
        
        // Show success modal
        showSuccessModal('Success!', 'Loan has been deleted successfully.');
      } else {
        throw new Error('Failed to delete loan');
      }
    } catch (error) {
      // Close confirmation modal
      closeConfirmModal();
      
      // Show error as success modal (you could create a separate error modal)
      showSuccessModal('Error', 'Failed to delete loan. Please try again.');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Handle server-side sorting
  const handleSort = (sortKey, sortDirection) => {
    setSortConfig({ key: sortKey, direction: sortDirection });
    fetchLoans(searchFilters, 0, sortKey, sortDirection);
  };

  // Get loans for current page
  // Server-side search
  const handleSearch = () => {
    fetchLoans(searchFilters, 0, sortConfig.key, sortConfig.direction);
    fetchAllLoans(searchFilters);
    fetchLoanSummary(searchFilters.status);
  };

  const handleReset = () => {
    const defaultFilters = {
      loanNumber: '',
      mobileNumber: '',
      holderName: '',
      status: 'A'  // Default to Active status
    };
    setSearchFilters(defaultFilters);
    setSortConfig({ key: null, direction: 'asc' });
    fetchLoans(defaultFilters, 0, null, null);
    fetchAllLoans(defaultFilters);
    fetchLoanSummary(defaultFilters.status);
  };

  const navigate = useNavigate();
  
  const handleAddNew = () => {
    navigate('/form'); 
  };

  const handleEdit = (loanId,loanNumber) => {
    navigate('/form', { 
      state: { 
        editLoan: true, 
        loanId: loanId ,
        loanNumber: loanNumber
      } 
    });
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      fetchLoans(searchFilters, currentPage - 1, sortConfig.key, sortConfig.direction);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      fetchLoans(searchFilters, currentPage + 1, sortConfig.key, sortConfig.direction);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    
    // Get status info from domain values
    const statusInfo = domainValues?.find(item => item.domainID === 10 && item.code === status);
    
    // Check if status is a display name and find the corresponding code
    let statusCode = status;
    if (status === 'Active') {
      statusCode = 'A';
    } else if (status === 'Closed') {
      statusCode = 'C';
    }
    
    // Use domain values to determine badge color
    if (statusCode === 'A' || status === 'Active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (statusCode === 'C' || status === 'Closed') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    
    // Default for unknown status
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  // Calculate record range for display
  const startRecord = totalRecords > 0 ? (currentPage * pageSize) + 1 : 0;
  const endRecord = Math.min((currentPage + 1) * pageSize, totalRecords);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-indigo-800 mb-4 md:mb-6">Loan Search</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">Loan Number</label>
            <input
              type="text"
              value={searchFilters.loanNumber}
              onChange={(e) => setSearchFilters({...searchFilters, loanNumber: e.target.value})}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              placeholder="Enter loan number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
            <input
              type="tel"
              value={searchFilters.mobileNumber}
              onChange={(e) => setSearchFilters({...searchFilters, mobileNumber: e.target.value})}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              placeholder="Enter mobile number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Loan Holder Name</label>
            <input
              type="text"
              value={searchFilters.holderName}
              onChange={(e) => setSearchFilters({...searchFilters, holderName: e.target.value})}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              placeholder="Enter holder name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Loan Status</label>
            <select
              value={searchFilters.status}
              onChange={(e) => setSearchFilters({...searchFilters, status: e.target.value})}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              disabled={!domainValuesLoaded}
            >
              <option value="">All Status</option>
              {!domainValuesLoaded ? (
                <option value="" disabled>Loading...</option>
              ) : (
                getLoanStatusOptions().map((option, index) => (
                  <option key={`loan-status-${option.value}-${index}`} value={option.value}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
            {searchFilters.status && (
              <p className="text-xs text-gray-500 mt-1">
                Showing summary for: {getLoanStatusOptions().find(option => option.value === searchFilters.status)?.label || searchFilters.status}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 md:mt-6">
          <button
            onClick={handleReset}
            className="w-full sm:w-auto px-4 md:px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm md:text-base"
          >
            Reset
          </button>
          <button
            onClick={handleSearch}
            className="w-full sm:w-auto px-4 md:px-6 py-2 bg-indigo-900 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base"
          >
            Search
          </button>
        </div>
      </div>

      {/* Loans Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 md:p-4 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center p-2 md:p-4 border-b border-gray-200 space-y-3 lg:space-y-0">
          <h2 className="text-xl md:text-2xl font-bold text-indigo-800">Loans</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="flex items-center gap-2 px-2 md:px-3 py-1 bg-green-100 text-green-800 rounded-lg border border-green-200 text-xs md:text-sm">
                <span className="font-semibold">Total Loan Amount:</span>
                {summaryLoading ? (
                  <span className="font-bold">Loading...</span>
                ) : (
                  <span className="font-bold">₹{loanSummary.totalLoanAmount?.toLocaleString() || '0'}</span>
                )}
              </div>
              <div className="flex items-center gap-2 px-2 md:px-3 py-1 bg-blue-100 text-blue-800 rounded-lg border border-blue-200 text-xs md:text-sm">
                <span className="font-semibold">Total Gold Weight:</span>
                {summaryLoading ? (
                  <span className="font-bold">Loading...</span>
                ) : (
                  <span className="font-bold">{loanSummary.totalGoldWeight?.toFixed(2) || '0'}g</span>
                )}
              </div>
              <div className="flex items-center gap-2 px-2 md:px-3 py-1 bg-purple-100 text-purple-800 rounded-lg border border-purple-200 text-xs md:text-sm">
                <span className="font-semibold">Total Loans:</span>
                {summaryLoading ? (
                  <span className="font-bold">Loading...</span>
                ) : (
                  <span className="font-bold">{loanSummary.totalLoans || '0'}</span>
                )}
              </div>
            </div>
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-indigo-900 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm md:text-base w-full sm:w-auto"
            >
              <Plus size={16} />
              Add New
            </button>
          </div>
        </div>
        
        <SortableGrid
          data={loans}
          columns={loanColumns}
          loading={loading}
          emptyMessage="No Records found."
          className="mb-4"
          sortConfig={sortConfig}
          onSort={handleSort}
        />
            
            {/* Enhanced Pagination */}
            {totalRecords > 0 && (
              <div className="px-2 md:px-6 py-3 md:py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between bg-gray-50 space-y-2 sm:space-y-0">
                <div className="flex items-center text-xs md:text-sm text-gray-700">
                  <span>
                    Showing {startRecord} to {endRecord} of {totalRecords} results
                  </span>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                  <div className="text-xs md:text-sm text-gray-700">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  
                  <div className="flex gap-1 md:gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 0}
                      className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <ChevronLeft size={14} className="md:w-4 md:h-4" />
                      Previous
                    </button>
                    
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages - 1}
                      className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-2 text-xs md:text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Next
                      <ChevronRight size={14} className="md:w-4 md:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

      {/* Custom Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={handleDelete}
        title="Delete Loan"
        message={`Are you sure you want to delete loan ${confirmModal.loanNumber}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteLoading === confirmModal.loanId}
      />

      {/* Custom Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={closeSuccessModal}
        title={successModal.title}
        message={successModal.message}
      />
    </div>
  );
};

export default LoanDashboard;
